import React from 'react';
import { useEffect } from 'react';
import { Stack } from '@mui/material';
import { useAuth } from 'react-oidc-context';
import VigoDateRange, { DateRangeShortcut, IVigoDateRangeReturn } from 'src/@vigo/vigo-daterange';
import { endOfDay, format, startOfDay } from 'date-fns';
import { calculateRange } from 'src/@core/utils/getDateRange';

interface DashboardDateFilterProps {
  onChange: (val: Array<Array<string>>) => void;
}

const DashboardDateFilter = ({ onChange }: DashboardDateFilterProps) => {
  const auth = useAuth();
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;

  const buildFilterString = (dateRange: IVigoDateRangeReturn): Array<Array<string>> => {
    if (dateRange?.dateFrom && dateRange?.dateTo) {
      if (isNaN(dateRange.dateFrom.getTime()) || (dateRange.dateTo && isNaN(dateRange.dateTo.getTime()))) {
        return [];
      }

      return [
        [`ge datetime'${format(dateRange.dateFrom, 'yyyy-MM-dd HH:mm:ss')}' and`],
        [`le datetime'${format(dateRange.dateTo, 'yyyy-MM-dd HH:mm:ss')}'`]
      ];
    }

    return [];
  };

  const handleDateRangeChange = ({ dateFrom, dateTo, selectedRange }: IVigoDateRangeReturn) => {
    localStorage.setItem('daterange-dashboard-' + loggedInUserId, JSON.stringify(selectedRange));
    onChange(buildFilterString({ dateFrom, dateTo, selectedRange }));
  };

  useEffect(() => {
    if (!loggedInUserId) return;

    const selectedRange = localStorage.getItem(`daterange-dashboard-${loggedInUserId}`)?.replace(/["]/g, '') ?? 'Today';

    const savedRange: Date[] = [];

    const { dateFrom, dateTo } = calculateRange(selectedRange, '');
    savedRange[0] = startOfDay(new Date(dateFrom ?? new Date()));
    savedRange[1] = endOfDay(new Date(dateTo ?? new Date()));
    if ((savedRange[0] && isNaN(savedRange[0]?.getTime())) || (savedRange[1] && isNaN(savedRange[1]?.getTime()))) {
      return;
    }

    onChange([
      [`ge datetime'${format(savedRange[0], 'yyyy-MM-dd HH:mm:ss')}' and`],
      [`le datetime'${format(savedRange[1], 'yyyy-MM-dd HH:mm:ss')}'`]
    ]);
  }, [loggedInUserId, onChange]);

  return (
    <Stack direction="row" alignItems="center">
      <VigoDateRange
        name="dashboardrange"
        selectedRange={localStorage.getItem(`daterange-dashboard-${loggedInUserId}`) ?? `Today`}
        allowCustom={true}
        showPast={true}
        variant="outlined"
        onChange={handleDateRangeChange}
        maxPastDays={30}
        maxFutureDays={30}
        preventInputDateEditing={true}
        disabledShortcuts={[DateRangeShortcut.Last3Months]}
      />
    </Stack>
  );
};

export default React.memo(DashboardDateFilter);
